import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { getVideoBySlug, getRelatedVideos } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoBySlug(id) : undefined;

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">内容未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = getRelatedVideos(video);

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    image: video.coverImage,
    datePublished: video.publishDate,
    author: {
      '@type': 'Organization',
      name: '黑桃视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '黑桃视频',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': typeof window !== 'undefined' ? window.location.href : '',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '黑桃视频', item: typeof window !== 'undefined' ? window.location.origin : '' },
      { '@type': 'ListItem', position: 2, name: video.categoryName, item: typeof window !== 'undefined' ? `${window.location.origin}/category/${video.category}` : '' },
      { '@type': 'ListItem', position: 3, name: video.title },
    ],
  };

  const viewCountDisplay = video.viewCount > 10000
    ? `${(video.viewCount / 10000).toFixed(1)}万`
    : video.viewCount;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      <Navbar />
      <main className="min-h-screen pt-16">
        <article className="max-w-[1440px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: video.category, to: `/category/${video.category}` },
              { label: video.title },
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-12">
            <div className="lg:col-span-2">
              <div className="relative overflow-hidden rounded-lg aspect-video mb-6">
                <img
                  src={video.coverImage}
                  alt={video.title}
                  title={`${video.title} - 黑桃视频高清免费在线观看`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group cursor-pointer">
                  <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-primary-500 rounded-full group-hover:scale-110 transition-transform">
                    <i className="ri-play-fill text-background-50 text-3xl md:text-4xl ml-1"></i>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-800">
                  {video.categoryName}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{video.rating}</strong>
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-eye-line"></i>
                  {viewCountDisplay} 次观看
                </span>
                <span className="text-sm text-foreground-500">{video.year}</span>
                <span className="text-sm text-foreground-500">{video.duration}</span>
                <span className="text-sm text-foreground-500">{video.publishDate}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                {video.title}
              </h1>

              <p className="text-base text-foreground-600 mb-6 leading-relaxed">
                {video.description}
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="border-t border-background-200/70 pt-6">
                <h2 className="text-lg font-heading font-semibold text-foreground-900 mb-4">内容详情</h2>
                <div
                  className="prose prose-sm max-w-none text-foreground-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: video.content }}
                />
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-20">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-fire-line text-accent-500"></i>
                  相关推荐
                </h3>
                <div className="space-y-4">
                  {relatedVideos.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.slug}`}
                      className="flex gap-3 group"
                    >
                      <div className="w-28 h-20 flex-shrink-0 rounded-md overflow-hidden">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {related.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-foreground-400">
                          <span>{related.categoryName}</span>
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                            {related.rating}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-8 p-5 rounded-lg bg-background-100">
                  <h4 className="text-sm font-heading font-semibold text-foreground-900 mb-3">关于黑桃视频</h4>
                  <p className="text-xs text-foreground-500 leading-relaxed mb-4">
                    黑桃视频提供高清流畅的视频观看体验，涵盖电影、电视剧、综艺、动漫、纪录片等多种类型，每日更新热门内容。
                  </p>
                  <Link
                    to="/about"
                    className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap"
                  >
                    了解更多 →
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}